/** What a **write** was refused for — the status and the code, not only the sentence.
 *
 * `DiffRefused` learned this lesson for one screen (UX-04): `new Error(detail.message)` loses the
 * one fact a screen has to branch on, and every refusal then renders as the same paragraph. The
 * same defect was still in six other places, and three of them are the ones EXP-29's second list
 * names — **an unauthorized direct request, a stale expected version, and a retry after an
 * ambiguous response are three different refusals that need three different remedies**, and a
 * client holding only a string cannot tell them apart.
 *
 * So this is the base class, and the feature-specific ones extend it rather than restating it.
 * `message` stays what the server wrote, because the server writes it once and two screens must not
 * describe the same fact differently (design rule 7); `code` and `status` are what a screen reads.
 */

import { NOT_A_NUMBER } from "../lib/decimalInput";

/** One refused field and the sentence for it, as the form should say it beside that field. */
export interface FieldSentence {
  /** The API's own name for the field (the last element of pydantic's `loc`), snake_case. */
  field: string;
  sentence: string;
}

/** Why a write was refused. A subclass adds fields; it never changes what these two mean. */
export class Refused extends Error {
  /** The HTTP status. **`0` means no response arrived at all**, which is a different thing from a
   * refusal and is why it is representable: see `isAmbiguous`. */
  readonly status: number;
  /** The API's own `detail.error`, or `""` when the body carried none. */
  readonly code: string;
  /** The version the row is actually on, when the server sent one with a `stale_version`. Shown
   * rather than withheld, for the reason `_require_version` gives: a client that has to guess
   * retries blindly. */
  readonly currentVersion: number | null;
  /** The reference the server put on this response (`X-Request-Id`, and `detail.request_id` on a
   * 500), or `""` when there was none - a gateway's own error carries none. Gate 35: a failure a
   * person can only describe ("it broke") is one we cannot find; a failure with a reference they
   * can read out is one line in the log. Shown beside the failure and never interpreted. */
  readonly requestId: string;
  /** Which fields a validator refused, each with a sentence (Q-06). Empty for a refusal that is not a
   * 422 list. A form puts each sentence beside its own field and moves focus to the first, so the
   * person does not have to read one paragraph and guess which box it means. */
  readonly fieldSentences: readonly FieldSentence[];
  /** The application itself wrote this answer (a code and a sentence), even though the status is one a
   * proxy also uses. A 503 that says "a provider key cannot be stored in this environment" is the
   * application declining, so nothing was saved and it is certain; a gateway's bare 503 is not. */
  readonly answeredByApplication: boolean;
  /** The roles the server said the action needs, on an `insufficient_role` 403 (`detail.needs`).
   * Empty for any other refusal. A screen names them (`NotYourRole`) so a refusal says who can. */
  readonly needs: readonly string[];

  constructor(
    status: number,
    code: string,
    message: string,
    currentVersion: number | null = null,
    requestId = "",
    fields: readonly FieldSentence[] = [],
    answeredByApplication = false,
    needs: readonly string[] = [],
  ) {
    super(message);
    this.name = "Refused";
    this.status = status;
    this.code = code;
    this.currentVersion = currentVersion;
    this.requestId = requestId;
    this.fieldSentences = fields;
    this.answeredByApplication = answeredByApplication;
    this.needs = needs;
  }

  /** The platform or the application said "too many" (gate 12). Retrying at once is the thing
   * being limited, so a screen must not offer it as the remedy. */
  get isRateLimited(): boolean {
    return this.status === 429;
  }

  /** This role may not do this. **Nothing was written, and retrying is useless** — so a screen
   * shows the sentence and no retry, which is design rule 6 arriving one step late (the control
   * should not have been there, and where it was reachable by URL this is what is left to say). */
  get isUnauthorized(): boolean {
    return this.status === 403;
  }

  /** The role is the reason: a 403 the guard `require_roles` wrote. Asking again changes nothing, so
   * a screen says who can and offers no retry (Q-05, design rule 6). An engagement's 403 carries
   * another code and is `OutsideEngagement`'s. */
  get isWrongRole(): boolean {
    return this.status === 403 && this.code === "insufficient_role";
  }

  /** **There is no usable session any more**, so retrying and reloading are both useless and the
   * only remedy is to sign in again.
   *
   * It earns a name because of where it bites hardest: first MFA enrolment runs on a `PENDING_MFA`
   * session, which lives **five minutes**. Somebody installing an authenticator app for the first
   * time can easily spend longer than that, come back, and meet a refusal — and the screen told
   * them *"try again, or reload this page"*, which is precisely the one thing that cannot work.
   * Same shape as the short-password 500: a remedy that is impossible. */
  get isSignedOut(): boolean {
    return this.status === 401;
  }

  /** The decision was made against a version that has since moved on (EXP-34). The remedy is
   * **reload and look at what changed**, which is the one remedy a generic failure never offers. */
  get isStale(): boolean {
    // `stale_closure` is the same refusal for a period (ADR-0094): a close or reopen decided
    // against a closure sequence that has since moved. The remedy is the same too.
    return this.code === "stale_version" || this.code === "stale_closure";
  }

  /** **We do not know whether the write landed.** A `0` is no response at all; a 502, 503 or 504 is
   * a proxy answering for an application that may well have finished the work; a 408 is the
   * request giving up on a server that did not. EXP-35's canonical case is exactly this — *a
   * request that timed out at a proxy and succeeded* — and the honest sentence says so rather than
   * inviting a retry that could double a figure. */
  get isAmbiguous(): boolean {
    // **Unless the application spoke, with a 503.** It sends a code and a sentence with every refusal it makes on
    // purpose, and a gateway never does. A 503 carrying both is "the vault is not configured", which is
    // a certain answer (every 503 this API raises says nothing was kept), and the generic "it is not known whether that was saved" over it hid the
    // reason (G-P8-26). A request that timed out has no body at all, so it stays unknown.
    if (this.answeredByApplication && this.status === 503) return false;
    return this.status === 0 || this.status === 408 || this.status >= 502;
  }
}

/** One closed period a write reached (ADR-0094). Read from `detail.periods[]` of a 409
 * `period_closed`, which lists **every** blocking period rather than the first. */
export interface ClosedPeriodRef {
  periodId: string;
  label: string;
  regimeKey: string;
  periodStart: string;
  periodEnd: string;
  closedAt: string;
}

/** A write into a closed period, refused — **ADR-0094**. A `Refused` that also carries *which*
 * periods and whether the caller already offered a reason but is not an Admin.
 *
 * Its own class because the generic one keeps a sentence and a code, and the two facts a screen
 * needs here are neither: the periods (to name them) and `needsAdmin` (to choose between "give a
 * reason" and "ask an Admin"). `message` stays what the server wrote, remedy included. */
export class PeriodClosedRefused extends Refused {
  readonly periods: readonly ClosedPeriodRef[];
  /** True when a reason was sent but the caller is not an Admin: a different person is the
   * remedy, not a different sentence. */
  readonly needsAdmin: boolean;

  constructor(message: string, periods: readonly ClosedPeriodRef[], needsAdmin: boolean) {
    super(409, "period_closed", message);
    this.name = "PeriodClosedRefused";
    this.periods = periods;
    this.needsAdmin = needsAdmin;
  }
}

export function isPeriodClosed(error: unknown): error is PeriodClosedRefused {
  return error instanceof PeriodClosedRefused;
}

/** The shape every one of this API's refusals has: `{"detail": {"error": ..., "message": ...}}`.
 *
 * Read defensively, because a refusal is the one response most likely **not** to be this: a 413
 * from a proxy or a 502 from a load balancer arrives as HTML, and reading it as JSON is how
 * "insufficient role" once became "that file could not be read".
 */
interface Detail {
  error: string;
  message: string;
  /** The next step, where the route sent one (DOC-01, EXP-05). Joined onto the sentence rather
   * than exposed separately: a refusal a person cannot act on produces a support conversation
   * instead of a second attempt that works, and no screen should have to remember to print it. */
  remedy: string;
  currentVersion: number | null;
  periods: ClosedPeriodRef[];
  needsAdmin: boolean;
  requestId: string;
  fields: FieldSentence[];
  /** The body carried both a code and a sentence: the application wrote this answer. */
  authored: boolean;
  /** `detail.needs` on an `insufficient_role`: the roles that may do this. */
  needs: string[];
}

/** "period_start" as a person would start a sentence: "Period start". */
function fieldWords(field: string): string {
  const words = field.replace(/_+/g, " ").trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

const NUMBER_SENTENCE = NOT_A_NUMBER;

/** A sentence for one entry of pydantic's validation list, or `null` for a type we have no words for.
 *
 * `value_error` is the application's own sentence (a `ValueError` a model raised for the person who
 * typed the value) and is used as written. The rest are pydantic's *kinds*, whose own English
 * ("Input should be a valid decimal") describes the request rather than the form, so each is said
 * once here, in the words of the screen, naming the field the way a label would. Anything not listed
 * returns `null` and the caller's fallback stays: a guess at a sentence is worse than the fallback
 * (a wrong reason is the thing this product refuses to show elsewhere). */
function sentenceFor(entry: { type?: unknown; msg?: unknown; ctx?: unknown }, field: string): string | null {
  const type = typeof entry.type === "string" ? entry.type : "";
  const ctx = (entry.ctx && typeof entry.ctx === "object" ? entry.ctx : {}) as Record<string, unknown>;
  const name = fieldWords(field || "value");
  const bound = (key: string) => (typeof ctx[key] === "number" || typeof ctx[key] === "string" ? String(ctx[key]) : "");
  switch (type) {
    case "value_error":
      return typeof entry.msg === "string" ? entry.msg.replace(/^Value error,\s*/, "") : null;
    case "missing":
      return `${name} is required.`;
    case "decimal_parsing":
    case "decimal_type":
    case "decimal_max_digits":
    case "decimal_max_places":
    case "decimal_whole_digits":
      return NUMBER_SENTENCE;
    case "int_parsing":
    case "int_type":
    case "int_from_float":
      return `${name} must be a whole number, such as 2022.`;
    case "string_too_short":
      return bound("min_length") ? `${name} needs at least ${bound("min_length")} characters.` : `${name} is too short.`;
    case "string_too_long":
      return bound("max_length") ? `${name} can be at most ${bound("max_length")} characters.` : `${name} is too long.`;
    case "date_parsing":
    case "date_type":
    case "date_from_datetime_parsing":
    case "date_from_datetime_inexact":
      return `${name} must be a date, such as 2026-01-31.`;
    case "uuid_parsing":
    case "uuid_type":
      return `Choose a value for ${name}.`;
    case "literal_error":
    case "enum":
      return `Choose one of the offered values for ${name}.`;
    case "greater_than":
      return bound("gt") ? `${name} must be more than ${bound("gt")}.` : null;
    case "greater_than_equal":
      return bound("ge") ? `${name} must be at least ${bound("ge")}.` : null;
    case "less_than":
      return bound("lt") ? `${name} must be less than ${bound("lt")}.` : null;
    case "less_than_equal":
      return bound("le") ? `${name} must be at most ${bound("le")}.` : null;
    default:
      return null;
  }
}

function closedPeriodsOf(raw: unknown): ClosedPeriodRef[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((entry: unknown) => {
    if (!entry || typeof entry !== "object") return [];
    const e = entry as Record<string, unknown>;
    const text = (key: string) => (typeof e[key] === "string" ? e[key] : "");
    return [
      {
        periodId: text("period_id"),
        label: text("label"),
        regimeKey: text("regime_key"),
        periodStart: text("period_start"),
        periodEnd: text("period_end"),
        closedAt: text("closed_at"),
      },
    ];
  });
}

function detailOf(error: unknown): Detail {
  const empty: Detail = {
    error: "",
    message: "",
    remedy: "",
    currentVersion: null,
    periods: [],
    needsAdmin: false,
    requestId: "",
    fields: [],
    authored: false,
    needs: [],
  };
  if (!error || typeof error !== "object" || !("detail" in error)) return empty;
  const detail: unknown = error.detail;
  // FastAPI's own validation errors send `detail` as a list, and a `HTTPException(detail="…")`
  // sends a string. Both are this API's shape minus the code, so the sentence survives and the
  // branch falls through to the generic one.
  if (typeof detail === "string") return { ...empty, message: detail };
  // **A validator's own refusal arrives as a list** (FastAPI's 422), and this function used to read
  // that as "no message", so a form showed its fallback ("That could not be recorded.") over the
  // one sentence that said what to change - the ambiguous unit 'gallons' on the manual entry form
  // (US walkthrough, C3). Only a `value_error` is used: its text is a sentence this product wrote
  // for the person who typed the value. Pydantic's own wording for the other kinds ("Field required",
  // "Input should be a valid UUID") describes the request, not the form, so those keep the caller's
  // fallback.
  if (Array.isArray(detail)) {
    const fields = detail.flatMap((item: unknown): FieldSentence[] => {
      if (!item || typeof item !== "object") return [];
      const entry = item as { type?: unknown; msg?: unknown; loc?: unknown; ctx?: unknown };
      const last: unknown = Array.isArray(entry.loc) ? (entry.loc as unknown[])[entry.loc.length - 1] : undefined;
      const field = typeof last === "string" ? last : "";
      const said = sentenceFor(entry, field);
      return said === null ? [] : [{ field, sentence: said }];
    });
    // Q-06: the common kinds are said in words now, not only `value_error`. The sentences are joined
    // once each, in order, so two refused fields read as two sentences and a repeated one is not
    // said twice (a number refused in four rows is one instruction).
    const sentences = [...new Set(fields.map((f) => f.sentence))];
    return fields.length > 0 ? { ...empty, message: sentences.join(" "), fields } : empty;
  }
  if (!detail || typeof detail !== "object") return empty;
  const body = detail as {
    error?: unknown;
    message?: unknown;
    remedy?: unknown;
    current_version?: unknown;
    periods?: unknown;
    needs_admin?: unknown;
    request_id?: unknown;
    needs?: unknown;
  };
  return {
    error: typeof body.error === "string" ? body.error : "",
    message: typeof body.message === "string" ? body.message : "",
    remedy: typeof body.remedy === "string" ? body.remedy : "",
    currentVersion: typeof body.current_version === "number" ? body.current_version : null,
    periods: closedPeriodsOf(body.periods),
    needsAdmin: body.needs_admin === true,
    requestId: typeof body.request_id === "string" ? body.request_id : "",
    fields: [],
    authored: typeof body.error === "string" && body.error !== "" && typeof body.message === "string" && body.message !== "",
    needs: Array.isArray(body.needs) ? body.needs.filter((role): role is string => typeof role === "string") : [],
  };
}

/** Build a `Refused` from what `openapi-fetch` hands back.
 *
 * `response` is required rather than optional, because the status is the whole point: a helper that
 * could be called without one would be called without one, and every refusal would come back a
 * `0` — which this class reads as *"we do not know whether it was written"*, the most alarming
 * sentence the product has, in front of an ordinary 422.
 */
export function refusalFrom(response: Response, error: unknown, fallback: string): Refused {
  const detail = detailOf(error);
  const sentence = [detail.message || platformSentence(response.status) || fallback, detail.remedy]
    .filter(Boolean)
    .join(" ");
  // A write into a closed period keeps its periods and its role fact (ADR-0094); the generic
  // class would keep a sentence, and a screen could not offer the Admin's way through.
  if (response.status === 409 && detail.error === "period_closed") {
    return new PeriodClosedRefused(sentence, detail.periods, detail.needsAdmin);
  }
  return new Refused(
    response.status,
    detail.error,
    sentence,
    detail.currentVersion,
    // The header first: it is on **every** response this API sends, including the ones whose body
    // was not this shape. The body's copy is for a 500 read through something that dropped headers.
    response.headers.get("x-request-id") || detail.requestId,
    detail.fields,
    detail.authored,
    detail.needs,
  );
}

/** What to say when **the platform** answered rather than the application (gate 35).
 *
 * API Gateway and Lambda have their own errors, and none is this API's shape: a bare
 * `{"message":"Too Many Requests"}`, an HTML 502, an empty 504, a 413 whose body is the string
 * `Request Entity Too Large`. `detailOf` finds no `detail` in any of them, so the caller's fallback
 * ("The filings issued could not be read.") was all a person saw - true, and silent about the two
 * cases where the remedy is *not* "try again": a request too large to ever be accepted, and a limit
 * to wait out. Those two get their own sentence; everything else keeps the caller's, which knows
 * what it was doing. Deliberately **no** sentence for 5xx: the caller's fallback names the thing
 * that failed, and a generic "the server had a problem" would replace it with less. */
function platformSentence(status: number): string {
  if (status === 429) return "Too many requests. Wait a minute, then try again.";
  if (status === 413) return "That is larger than the service accepts. Nothing was saved.";
  return "";
}

/** Whether a thrown value is a write whose outcome is **unknown**.
 *
 * Two shapes, and the second is the one no `Refused` can carry: `fetch` rejects with a `TypeError`
 * when the request never completed, so nothing in this module ever sees it. A client that treated
 * that as a failure would be asserting the write did not happen, which it does not know.
 */
export function isAmbiguous(error: unknown): boolean {
  if (error instanceof Refused) return error.isAmbiguous;
  return error instanceof TypeError;
}

/** Whether a thrown value means **there is no session any more**.
 *
 * Deliberately *not* true for a `TypeError`, unlike `isAmbiguous`: a request that never completed
 * tells us nothing about the session, and sending somebody to sign in again because their wifi
 * dropped would discard a perfectly good one — and, during first MFA enrolment, start a fresh
 * five-minute window they did not need.
 */
export function isSignedOut(error: unknown): boolean {
  return error instanceof Refused && error.isSignedOut;
}
